import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";
import { FETCH_DATE_AFTER_SELECTION } from "helper/Functions";
import { API_URL } from "helper/Constants";
import CustomTable from "../../../components/ui/tables/CustomTable";
import SelectedDates from "../FilterFormComponents/DateFilter";
import { ReactComponent as IconSearch } from "../../../assets/icons/icon-search.svg";
import { useLocation } from "react-router-dom";
import SectionLoader from "components/ui/loaders/SectionLoader";
import TransferService from "service/TransferService";
import { useAuth } from "provider/AuthContext";
import PaginatedRoleBasedDropdowns from "../FilterFormComponents/PaginatedRoleBasedDropdown";

const TransferHistory = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState("today");
  const [chosenDate, setChosenDate] = useState("");
  const [selectedUser, setSelectedUser] = useState(user?.username || null);
  const [transfers, setTransfers] = useState([]);
  const [showReportData, setShowReportData] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [date, setDate] = useState({ startDate: "", endDate: "" });
  const [columnVisibility, setColumnVisibility] = useState({});
  const [selectedRole, setSelectedRole] = useState({});
  const [selectedInputValues, setSelectedInputValues] = useState({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [totalCount, setTotalCount] = useState(0);
  const [sorting, setSorting] = useState([{ id: "type", desc: false }]); // Default sort by type (deposits first)
  const transferServ = new TransferService(API_URL);

  const location = useLocation();
  const username = location.state?.username;
  const selectedUserId = location.state?.selectedUserId;

  useEffect(() => {
    console.log(selectedInputValues, "SelectedInputValues");
  }, [selectedInputValues]);

  useEffect(() => {
    if (username) {
      setSelectedRole({ role: { value: "User", label: "User" } });
      setSelectedInputValues({
        User: { value: selectedUserId, label: username },
      });
    }
  }, [username, selectedUserId]);

  const fetchTransferHistory = async (userId) => {
    setLoading(true);
    try {
      let dateOption;

      if (selectedDate === "custom" && chosenDate) {
        const { startDate, endDate } = chosenDate;
        if (!startDate || !endDate) {
          setErrorMessage("Invalid date range selected.");
          return;
        }

        dateOption = { startDate, endDate };
      } else {
        dateOption = selectedDate;
      }

      // const username = selectedUser && selectedUser !== "all" ? selectedUser : user.username;

      const {
        Partner: partner = null,
        SuperAgent: superAgent = null,
        Agent: agent = null,
        User: userInput = null,
      } = selectedInputValues;

      const selectedData = {
        partnerId: partner?.value || undefined,
        superAgentId: superAgent?.value || undefined,
        agentId: agent?.value || undefined,
        userId: userInput?.value || undefined,
      };

      const result = await transferServ.getTransferHistory(selectedData, date);

      if (result.success) {
        // Custom sorting: deposits first, then by date (newest first)
        let sortedTransfers = result.transferHistory.sort((a, b) => {
          // First, sort by type (deposit comes before withdraw)
          if (a.type !== b.type) {
            return a.type === "deposit" ? -1 : 1;
          }
          // Then sort by date (newest first)
          return new Date(b.date) - new Date(a.date);
        });
        setTransfers(sortedTransfers);
        setTotalCount(result.transferHistory.length);
        setShowReportData(true);
        // setSelectedInputValues({})
      } else {
        console.error("Error from API:", result.message);
        setErrorMessage(result.message);
        setShowReportData(false);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching transfer history:", error);
      setErrorMessage("An error occurred while fetching transfer history.");
      setShowReportData(false);
      setLoading(false);
    }
  };

  // Removed useEffect for fetchTransferHistory
  const handleSearch = () => {
    const selections = Object.values(selectedInputValues || {});
    const lastSelectedUserId = selections.length
      ? selections[selections.length - 1]?.value
      : null;
    fetchTransferHistory(lastSelectedUserId);
  };

  useEffect(() => {
    const { startDate, endDate } = FETCH_DATE_AFTER_SELECTION(
      selectedDate,
      chosenDate
    );
    setDate((prevDate) => ({
      ...prevDate,
      startDate: startDate,
      endDate: endDate,
    }));
  }, [selectedDate, chosenDate]);

  const handleReset = () => {
    setSelectedDate("today");
    setChosenDate("");
    setTransfers([]);
    setSelectedUser(user?.username || null);
    setShowReportData(false);
    setErrorMessage(null);
    setSelectedInputValues({});
    setSelectedRole({});
    setPagination({ pageIndex: 0, pageSize: 10 });
    setSorting([{ id: "type", desc: false }]); // Reset to default sorting
  };

  const tableData = transfers?.map((item, index) => ({
    ...item,
    senderRole: item.senderId?.role || "",
    receiverRole: item.receiverId?.role || "",
  }));

  const filteredTableData = useMemo(() => {
    const selections = Object.values(selectedInputValues || {});
    if (!selections.length) return tableData;
    const selectedUsernames = selections
      .map((s) => s?.label?.toLowerCase())
      .filter(Boolean);
    if (!selectedUsernames.length) return tableData;
    return tableData?.filter((row) => {
      const sender = row.senderId?.username?.toLowerCase();
      const receiver = row.receiverId?.username?.toLowerCase();
      return selectedUsernames.every(
        (uname) => sender === uname || receiver === uname
      );
    });
  }, [tableData, selectedInputValues]);

  const tableColumns = useMemo(
    () => [
      {
        accessorKey: "serialNo",
        header: "Sr. No.",
        enableHiding: false,
        size: 100,
        Cell: ({ cell }) => <>{cell.row.index + 1}</>,
      },
      {
        accessorKey: "senderId.username",
        header: "Sender",
        enableHiding: false,
        enableSorting: true,
        size: 100,
        Cell: ({ cell }) => (
          <>
            {cell.row.original.senderId?.username || "N/A"} (
            {cell.row.original.senderId?.role})
          </>
        ),
      },
      {
        accessorKey: "senderRole",
        header: "Sender Role",
        enableHiding: true,
        size: 0,
        Cell: ({ cell }) => null,
      },
      {
        accessorKey: "receiverId.username",
        header: "Receiver",
        enableHiding: false,
        enableSorting: true,
        size: 100,
        Cell: ({ cell }) => (
          <>
            {cell.row.original.receiverId?.username || "N/A"} (
            {cell.row.original.receiverId?.role})
          </>
        ),
      },
      {
        accessorKey: "receiverRole",
        header: "Receiver Role",
        enableHiding: true,
        size: 0,
        Cell: ({ cell }) => null,
      },
      {
        accessorKey: "type",
        header: "Type",
        enableHiding: false,
        enableSorting: true,
        size: 50,
        Cell: ({ cell }) => (
          <span className="capitalize">{cell.row.original.type}</span>
        ),
        sortingFn: (rowA, rowB, columnId) => {
          const aValue = rowA.getValue(columnId);
          const bValue = rowB.getValue(columnId);
          // Custom sort: deposit comes before withdraw
          if (aValue === "deposit" && bValue === "withdraw") return -1;
          if (aValue === "withdraw" && bValue === "deposit") return 1;
          return aValue.localeCompare(bValue);
        },
      },
      {
        accessorKey: "amount",
        header: "Amount",
        enableHiding: false,
        enableSorting: true,
        size: 50,
        Cell: ({ cell }) => Math.abs(cell.row.original.amount).toFixed(2),
        sortingFn: (rowA, rowB, columnId) => {
          const aValue = Math.abs(rowA.getValue(columnId));
          const bValue = Math.abs(rowB.getValue(columnId));
          return aValue - bValue;
        },
      },
      {
        accessorKey: "balanceBefore.sender",
        header: "Sender Balance Before",
        size: 179,
        Cell: ({ cell }) => (
          <>
            {cell.row.original.balanceBefore?.sender != null
              ? Math.abs(cell.row.original.balanceBefore.sender).toFixed(2)
              : "N/A"}
          </>
        ),
      },
      {
        accessorKey: "balanceAfter.sender",
        header: "Sender Balance After",
        size: 179,
        Cell: ({ cell }) => (
          <>
            {cell.row.original.balanceAfter?.sender != null
              ? Math.abs(cell.row.original.balanceAfter.sender).toFixed(2)
              : "N/A"}
          </>
        ),
      },
      {
        accessorKey: "balanceBefore.receiver",
        header: "Receiver Balance Before",
        size: 179,
        Cell: ({ cell }) => (
          <>
            {cell.row.original.balanceBefore?.receiver != null
              ? Math.abs(cell.row.original.balanceBefore.receiver).toFixed(2)
              : "N/A"}
          </>
        ),
      },
      {
        accessorKey: "balanceAfter.receiver",
        header: "Receiver Balance After",
        size: 179,
        Cell: ({ cell }) => (
          <>
            {cell.row.original.balanceAfter?.receiver != null
              ? Math.abs(cell.row.original.balanceAfter.receiver).toFixed(2)
              : "N/A"}
          </>
        ),
      },
      {
        accessorKey: "date",
        header: "Date",
        enableHiding: false,
        enableSorting: true,
        size: 100,
        Cell: ({ cell }) =>
          moment
            ?.tz(cell.row.original?.date, "UTC")
            ?.format("YYYY-MM-DD HH:mm:ss"),
        sortingFn: (rowA, rowB, columnId) => {
          const aValue = new Date(rowA.getValue(columnId));
          const bValue = new Date(rowB.getValue(columnId));
          return aValue - bValue;
        },
      },
    ],
    []
  );

  useEffect(() => {
    setColumnVisibility({
      "balanceBefore.sender": false,
      "balanceBefore.receiver": false,
      "balanceAfter.sender": false,
      "balanceAfter.receiver": false,
      senderRole: false,
      receiverRole: false,
    });
  }, [setColumnVisibility]);

  console.log(selectedInputValues, "selectedInputValues in TransferHistory");

  return (
    <div className="flex flex-col w-full transfer-history">
      <header className="rounded-lg sm:text-start text-center bg-[#474747] text-white w-full p-3">
        <h1 className="md:text-2xl text-lg font-bold md:text-left text-center">
          Transfer History
        </h1>
      </header>
      <div className="flex-1 pt-4">
        <div className="w-full rounded-lg">
          <SelectedDates
            title="Transaction Date"
            setSelectedDate={setSelectedDate}
            selectedDate={selectedDate}
            setCustomDate={setChosenDate}
            setShowReportData={setShowReportData}
          />
          <div className="flex flex-row gap-3 mt-4">
            <PaginatedRoleBasedDropdowns
              baseRole={user?.role}
              selectedRole={selectedRole}
              setSelectedRole={setSelectedRole}
              selectedInputValues={selectedInputValues}
              setSelectedInputValues={setSelectedInputValues}
            />
          </div>

          <div className="lg:flex gap-3 mb-8 mt-7">
            <button
              className="bg-primary hover:bg-primary/80 p-2 text-black w-full lg:max-w-40 font-bold lg:py-2 px-11 rounded-lg"
              type="button"
              onClick={handleSearch}
            >
              SEARCH
            </button>
            <button
              className="border border-black hover:bg-gray-300 p-2 mt-2 sm:mt-0 text-gray-700 lg:max-w-40 font-bold w-full lg:py-2 px-11 rounded-lg"
              type="reset"
              onClick={handleReset}
            >
              RESET
            </button>
          </div>

          <hr className="border-t mt-5 mb-3 border-[#d4c8c8a3]" />

          {!loading ? (
            showReportData ? (
              <CustomTable
                columns={tableColumns}
                data={filteredTableData ? filteredTableData : []}
                columnVisibility={columnVisibility}
                setColumnVisibility={setColumnVisibility}
                enableGlobalFilter={true}
                enableTopToolbar={true}
                // enableBottomToolbar={true}
                // enableHiding={true}
                enableSorting={true}
                enableColumnActions={true}
                // enableColumnFilters={true}
                // enableDensityToggle={true}
                // enableFullScreenToggle={true}
                setPagination={setPagination}
                totalCount={totalCount}
                pagination={pagination}
                sorting={sorting}
                setSorting={setSorting}
                muiPaginationProps={{
                  rowsPerPageOptions: [5, 10, 20, 50],
                  showFirstButton: true,
                  showLastButton: true,
                }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-6 px-3 my-7">
                <div className="bg-[#EDEEF5] rounded-lg p-6 sm:p-8 [&_svg_path]:fill-slate-500">
                  <IconSearch />
                </div>
                <span className="text-lg sm:text-xl mt-8 mb-4 text-[#474747] text-center w-full max-w-[300px]">
                  Search all available records by selecting one of the filters
                  above!
                </span>
              </div>
            )
          ) : (
            <div className="flex justify-center py-8">
              <SectionLoader bgColor="light" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransferHistory;
